import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import "./form.css";

const API_BASE_URL = `${
  process.env.REACT_APP_API_URL || "http://localhost:4000"
}/api`;

const initialForm = {
  employee_id: "",
  employee_name: "",
  gender: "",
  dob: "",
  mobile: "",
  email: "",
  division: "",
  project: "",
  department_client: "",
  designation: "",
  joining_date: "",
  employment_status: "",
  exit_date: "",
  uan: "",
  esic_ip_no: "",
  pf_applicable: "No",
  esic_applicable: "No",
  ctc_amount: "",
  documents_status: "",
  bank_verification: "",
  pf_status: "",
  esic_status: "",
  aadhaar_card_no: "",
  pan_no: "",
  bank_name: "",
  account_number: "",
  ifsc: "",
  account_holder_name: "",
  attendance_status: "",
  remarks: "",
};

const getRecordList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.records)) return data.records;
  return [];
};

const formatDate = (value) =>
  value ? String(value).substring(0, 10) : "";

const getPdfFileName = (record) => {
  if (record.document_pdf_name) {
    return record.document_pdf_name;
  }

  const employeeName = String(
    record.employee_name || "Employee"
  ).replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");

  const employeeId = String(
    record.employee_id || record.id || ""
  ).replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");

  return `${employeeName}_${employeeId}.pdf`;
};

export default function Form() {
  const [form, setForm] = useState({ ...initialForm });
  const [records, setRecords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [recordsFullScreen, setRecordsFullScreen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);

  const getForms = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/jmk-form`
      );

      setRecords(getRecordList(response.data));
    } catch (error) {
      console.error("Get employee records error:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to load employee records"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getForms();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedPdf(null);
      return;
    }

    if (
      file.type !== "application/pdf" ||
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      toast.error("Please select a PDF file only");
      e.target.value = "";
      setSelectedPdf(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("PDF size must not exceed 10 MB");
      e.target.value = "";
      setSelectedPdf(null);
      return;
    }

    setSelectedPdf(file);
  };

  const resetForm = () => {
    setForm({ ...initialForm });
    setEditingId(null);
    setSelectedPdf(null);

    const input = document.getElementById(
      "employee-documents-pdf"
    );

    if (input) {
      input.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.employee_id.trim()) {
      toast.error("Employee ID is required");
      return;
    }

    if (!form.employee_name.trim()) {
      toast.error("Employee Name is required");
      return;
    }

    try {
      setLoading(true);

      let employeeRecordId = editingId;

      // Save or update employee details first.
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/jmk-form/${editingId}`,
          form
        );
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/jmk-form`,
          form
        );

        employeeRecordId =
          response.data?.id ??
          response.data?.data?.id ??
          response.data?.record?.id ??
          null;

        // If the create response does not contain the database ID,
        // find the employee in the records list.
        if (!employeeRecordId) {
          const listResponse = await axios.get(
            `${API_BASE_URL}/jmk-form`
          );

          const list = getRecordList(listResponse.data);

          const match = list.find(
            (item) =>
              String(item.employee_id) ===
              String(form.employee_id)
          );

          employeeRecordId = match?.id ?? null;
        }
      }

      // Upload the selected PDF to the backend/S3.
      if (selectedPdf) {
        if (!employeeRecordId) {
          throw new Error(
            "Employee was saved, but the record ID was not found. Please reload the records and try uploading the PDF from Edit."
          );
        }

        const pdfData = new FormData();
        pdfData.append("document", selectedPdf);

        await axios.post(
          `${API_BASE_URL}/jmk-form/${employeeRecordId}/document`,
          pdfData
        );
      }

      toast.success(
        editingId
          ? "Employee details updated successfully"
          : "Employee added successfully"
      );

      resetForm();
      await getForms();
    } catch (error) {
      console.error("Save employee error:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save employee details"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);

    setForm({
      employee_id: record.employee_id || "",
      employee_name: record.employee_name || "",
      gender: record.gender || "",
      dob: formatDate(record.dob),
      mobile: record.mobile || "",
      email: record.email || "",
      division: record.division || "",
      project: record.project || "",
      department_client: record.department_client || "",
      designation: record.designation || "",
      joining_date: formatDate(record.joining_date),
      employment_status: record.employment_status || "",
      exit_date: formatDate(record.exit_date),
      uan: record.uan || "",
      esic_ip_no: record.esic_ip_no || "",
      pf_applicable: record.pf_applicable || "No",
      esic_applicable: record.esic_applicable || "No",
      ctc_amount: record.ctc_amount ?? "",
      documents_status: record.documents_status || "",
      bank_verification: record.bank_verification || "",
      pf_status: record.pf_status || "",
      esic_status: record.esic_status || "",
      aadhaar_card_no: record.aadhaar_card_no || "",
      pan_no: record.pan_no || "",
      bank_name: record.bank_name || "",
      account_number: record.account_number || "",
      ifsc: record.ifsc || "",
      account_holder_name: record.account_holder_name || "",
      attendance_status: record.attendance_status || "",
      remarks: record.remarks || "",
    });

    // Keep the existing PDF unless a new PDF is selected.
    setSelectedPdf(null);

    const input = document.getElementById(
      "employee-documents-pdf"
    );

    if (input) {
      input.value = "";
    }

    setRecordsFullScreen(false);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this employee?"
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      await axios.delete(
        `${API_BASE_URL}/jmk-form/${id}`
      );

      toast.success("Employee deleted successfully");

      if (editingId === id) {
        resetForm();
      }

      await getForms();
    } catch (error) {
      console.error("Delete employee error:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to delete employee"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  const filteredRecords = records.filter((record) => {
    const searchValue = search.toLowerCase().trim();

    if (!searchValue) return true;

    return [
      record.employee_id,
      record.employee_name,
      record.mobile,
      record.email,
      record.designation,
      record.project,
      record.division,
      record.department_client,
    ].some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(searchValue)
    );
  });

  // Get the signed S3 URL from the backend.
  const getEmployeePdfUrl = async (record) => {
    if (!record.document_pdf_key) {
      throw new Error(
        "No PDF uploaded for this employee"
      );
    }

    const response = await axios.get(
      `${API_BASE_URL}/jmk-form/${record.id}/document`
    );

    const url = response.data?.url;

    if (!url) {
      throw new Error(
        "PDF URL was not returned by the server"
      );
    }

    return url;
  };

  const viewPdf = async (record) => {
    if (!record.document_pdf_key) {
      toast.error("No PDF uploaded for this employee");
      return;
    }

    try {
      const url = await getEmployeePdfUrl(record);

      window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error("PDF view error:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to open employee PDF"
      );
    }
  };

  const downloadPdf = async (record) => {
    if (!record.document_pdf_key) {
      toast.error("No PDF uploaded for this employee");
      return;
    }

    try {
      const url = await getEmployeePdfUrl(record);

      const link = document.createElement("a");
      link.href = url;
      link.download = getPdfFileName(record);
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("PDF download requested");
    } catch (error) {
      console.error("PDF download error:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to download employee PDF"
      );
    }
  };

  const exportRecords = () => {
    if (!filteredRecords.length) {
      toast.error("No employee records available to export");
      return;
    }

    const headers = [
      "Employee ID",
      "Employee Name",
      "Gender",
      "DOB",
      "Mobile",
      "Email",
      "Division",
      "Project",
      "Department / Client",
      "Designation",
      "Joining Date",
      "Employment Status",
      "Exit Date",
      "UAN",
      "ESIC IP No.",
      "PF Applicable",
      "ESIC Applicable",
      "CTC Amount",
      "Documents Status",
      "Bank Verification",
      "PF Status",
      "ESIC Status",
      "Aadhaar Card No.",
      "PAN No.",
      "Bank Name",
      "Account Number",
      "IFSC",
      "Account Holder Name",
      "Attendance Status",
      "Remarks",
      "Employee PDF",
    ];

    const rows = filteredRecords.map((record) => [
      record.employee_id,
      record.employee_name,
      record.gender,
      formatDate(record.dob),
      record.mobile,
      record.email,
      record.division,
      record.project,
      record.department_client,
      record.designation,
      formatDate(record.joining_date),
      record.employment_status,
      formatDate(record.exit_date),
      record.uan,
      record.esic_ip_no,
      record.pf_applicable,
      record.esic_applicable,
      record.ctc_amount,
      record.documents_status,
      record.bank_verification,
      record.pf_status,
      record.esic_status,
      record.aadhaar_card_no,
      record.pan_no,
      record.bank_name,
      record.account_number,
      record.ifsc,
      record.account_holder_name,
      record.attendance_status,
      record.remarks,
      record.document_pdf_name || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (value) =>
              `"${String(value ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      )
      .join("\r\n");

    const blob = new Blob(
      ["\uFEFF", csvContent],
      { type: "text/csv;charset=utf-8;" }
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `employee-records-${new Date()
      .toISOString()
      .substring(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);

    toast.success("Employee records exported successfully");
  };

  const openEmployeeRecords = () => {
    setSearch("");
    setRecordsFullScreen(true);
  };

  return (
    <div className="jmk-form-page">
      <div className="jmk-form-container">
        <div className="page-header">
          <div className="page-title-area">
            <h1>Employee Master</h1>
            <p>
              Manage employee information, statutory details,
              banking and attendance records.
            </p>
          </div>

          <div className="dashboard-record-actions">
            <div className="record-count">
              <span>{records.length}</span>
              <small>Total Employees</small>
            </div>

            <button
              type="button"
              className="employee-records-button"
              onClick={openEmployeeRecords}
            >
              <div className="employee-records-content">
                <strong>Employee Records</strong>
                <span>View all records</span>
              </div>
              <div className="employee-records-arrow">→</div>
            </button>
          </div>
        </div>

        <form className="employee-form" onSubmit={handleSubmit}>
          <FormSection title="Employee Information" number="01">
            <FormInput label="Employee ID" name="employee_id" value={form.employee_id} onChange={handleChange} required />
            <FormInput label="Employee Name" name="employee_name" value={form.employee_name} onChange={handleChange} required />
            <FormSelect label="Gender" name="gender" value={form.gender} onChange={handleChange} options={["Male", "Female", "Other"]} />
            <FormInput label="DOB" name="dob" type="date" value={form.dob} onChange={handleChange} />
            <FormInput label="Mobile" name="mobile" value={form.mobile} onChange={handleChange} />
            <FormInput label="Email" name="email" type="email" value={form.email} onChange={handleChange} />
            <FormInput label="Division" name="division" value={form.division} onChange={handleChange} />
            <FormInput label="Project" name="project" value={form.project} onChange={handleChange} />
            <FormInput label="Department / Client" name="department_client" value={form.department_client} onChange={handleChange} />
            <FormInput label="Designation" name="designation" value={form.designation} onChange={handleChange} />
            <FormInput label="Joining Date" name="joining_date" type="date" value={form.joining_date} onChange={handleChange} />
            <FormSelect label="Employment Status" name="employment_status" value={form.employment_status} onChange={handleChange} options={["Active", "Inactive", "On Notice", "Exited"]} />
            <FormInput label="Exit Date" name="exit_date" type="date" value={form.exit_date} onChange={handleChange} />
          </FormSection>

          <FormSection title="Statutory & Payroll Information" number="02">
            <FormInput label="UAN" name="uan" value={form.uan} onChange={handleChange} />
            <FormInput label="ESIC IP No." name="esic_ip_no" value={form.esic_ip_no} onChange={handleChange} />
            <FormSelect label="PF Applicable" name="pf_applicable" value={form.pf_applicable} onChange={handleChange} options={["Yes", "No"]} />
            <FormSelect label="ESIC Applicable" name="esic_applicable" value={form.esic_applicable} onChange={handleChange} options={["Yes", "No"]} />
            <FormInput label="CTC Amount" name="ctc_amount" type="number" value={form.ctc_amount} onChange={handleChange} />
            <FormSelect label="Documents Status" name="documents_status" value={form.documents_status} onChange={handleChange} options={["Complete", "Pending", "Partial"]} />
            <FormSelect label="Bank Verification" name="bank_verification" value={form.bank_verification} onChange={handleChange} options={["Verified", "Pending", "Rejected"]} />
            <FormSelect label="PF Status" name="pf_status" value={form.pf_status} onChange={handleChange} options={["Active", "Inactive", "Pending", "Not Applicable"]} />
            <FormSelect label="ESIC Status" name="esic_status" value={form.esic_status} onChange={handleChange} options={["Active", "Inactive", "Pending", "Not Applicable"]} />
          </FormSection>

          <FormSection title="Identity & Bank Details" number="03">
            <FormInput label="Aadhaar Card No." name="aadhaar_card_no" value={form.aadhaar_card_no} onChange={handleChange} />
            <FormInput label="PAN No." name="pan_no" value={form.pan_no} onChange={handleChange} />
            <FormInput label="Bank Name" name="bank_name" value={form.bank_name} onChange={handleChange} />
            <FormInput label="Account Number" name="account_number" value={form.account_number} onChange={handleChange} />
            <FormInput label="IFSC" name="ifsc" value={form.ifsc} onChange={handleChange} />
            <FormInput label="Account Holder Name" name="account_holder_name" value={form.account_holder_name} onChange={handleChange} />
          </FormSection>

          <FormSection title="Attendance & Remarks" number="04">
            <FormSelect label="Attendance Status" name="attendance_status" value={form.attendance_status} onChange={handleChange} options={["Regular", "Irregular", "On Leave", "Not Available"]} />

            <div className="form-field full-width">
              <label htmlFor="remarks">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                placeholder="Enter remarks"
                rows="4"
              />
            </div>
          </FormSection>

          <FormSection title="Employee Documents" number="05">
            <div className="form-field full-width">
              <label htmlFor="employee-documents-pdf">
                Combined Employee Documents (PDF)
              </label>

              <input
                id="employee-documents-pdf"
                type="file"
                accept=".pdf,application/pdf"
                onChange={handlePdfChange}
              />

              <small>
                Upload one PDF containing all employee documents.
                Maximum size: 10 MB.
              </small>

              {selectedPdf && (
                <p className="selected-pdf-name">
                  Selected: {selectedPdf.name}
                </p>
              )}

              {editingId && (
                <small>
                  Leave the field empty to keep the existing PDF.
                </small>
              )}
            </div>
          </FormSection>

          <div className="form-actions">
            {editingId && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : editingId
                ? "Update Employee"
                : "Save Employee"}
            </button>
          </div>
        </form>
      </div>

      {recordsFullScreen && (
        <div className="records-fullscreen">
          <div className="fullscreen-header">
            <div className="fullscreen-title">
              <h1>Employee Records</h1>
              <p>Complete employee master records</p>
            </div>

            <div className="fullscreen-actions">
              <button
                type="button"
                className="fullscreen-export-btn"
                onClick={exportRecords}
              >
                ↓ Export
              </button>

              <button
                type="button"
                className="fullscreen-back-btn"
                onClick={() => setRecordsFullScreen(false)}
              >
                ← Back
              </button>
            </div>
          </div>

          <div className="fullscreen-toolbar">
            <div className="fullscreen-count">
              <strong>{filteredRecords.length}</strong>
              <span>Employee Records</span>
            </div>

            <input
              type="text"
              className="fullscreen-search"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="fullscreen-table-wrapper">
            <table className="employee-table fullscreen-table">
              <thead>
                <tr>
                  {[
                    "#",
                    "Employee ID",
                    "Employee Name",
                    "Gender",
                    "DOB",
                    "Mobile",
                    "Email",
                    "Division",
                    "Project",
                    "Department / Client",
                    "Designation",
                    "Joining Date",
                    "Status",
                    "Exit Date",
                    "UAN",
                    "ESIC IP No.",
                    "PF",
                    "ESIC",
                    "CTC",
                    "Documents",
                    "Bank Verification",
                    "PF Status",
                    "ESIC Status",
                    "Aadhaar",
                    "PAN",
                    "Bank Name",
                    "Account Number",
                    "IFSC",
                    "Account Holder",
                    "Attendance",
                    "Remarks",
                    "Employee PDF",
                    "Actions",
                  ].map((heading) => (
                    <th key={heading} scope="col">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={33} className="empty-row">
                      {loading
                        ? "Loading records..."
                        : "No employee records found."}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record, index) => (
                    <tr key={record.id}>
                      <td>{index + 1}</td>
                      <td className="employee-code">
                        {record.employee_id || "-"}
                      </td>
                      <td className="employee-name">
                        {record.employee_name || "-"}
                      </td>
                      <td>{record.gender || "-"}</td>
                      <td>{formatDate(record.dob) || "-"}</td>
                      <td>{record.mobile || "-"}</td>
                      <td>{record.email || "-"}</td>
                      <td>{record.division || "-"}</td>
                      <td>{record.project || "-"}</td>
                      <td>{record.department_client || "-"}</td>
                      <td>{record.designation || "-"}</td>
                      <td>
                        {formatDate(record.joining_date) || "-"}
                      </td>

                      <td>
                        <span
                          className={`status-badge ${(record.employment_status || "")
                            .toLowerCase()
                            .replace(/\s/g, "-")}`}
                        >
                          {record.employment_status || "-"}
                        </span>
                      </td>

                      <td>{formatDate(record.exit_date) || "-"}</td>
                      <td>{record.uan || "-"}</td>
                      <td>{record.esic_ip_no || "-"}</td>
                      <td>{record.pf_applicable || "No"}</td>
                      <td>{record.esic_applicable || "No"}</td>
                      <td>
                        ₹{" "}
                        {Number(
                          record.ctc_amount || 0
                        ).toLocaleString("en-IN")}
                      </td>
                      <td>{record.documents_status || "-"}</td>
                      <td>{record.bank_verification || "-"}</td>
                      <td>{record.pf_status || "-"}</td>
                      <td>{record.esic_status || "-"}</td>
                      <td>{record.aadhaar_card_no || "-"}</td>
                      <td>{record.pan_no || "-"}</td>
                      <td>{record.bank_name || "-"}</td>
                      <td>{record.account_number || "-"}</td>
                      <td>{record.ifsc || "-"}</td>
                      <td>{record.account_holder_name || "-"}</td>
                      <td>{record.attendance_status || "-"}</td>
                      <td className="remarks-cell">
                        {record.remarks || "-"}
                      </td>

                      <td>
                        {record.document_pdf_key ? (
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit"
                              onClick={() => viewPdf(record)}
                              disabled={loading}
                            >
                              View PDF
                            </button>

                            <button
                              type="button"
                              className="action-edit"
                              onClick={() => downloadPdf(record)}
                              disabled={loading}
                            >
                              Download
                            </button>
                          </div>
                        ) : (
                          <span>No PDF uploaded</span>
                        )}
                      </td>

                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="action-edit"
                            onClick={() => handleEdit(record)}
                            disabled={loading}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="action-delete"
                            onClick={() => handleDelete(record.id)}
                            disabled={loading}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FormSection({ title, number, children }) {
  return (
    <div className="form-section">
      <div className="section-header">
        <h2>{title}</h2>
        <span>{number}</span>
      </div>

      <div className="form-grid">{children}</div>
    </div>
  );
}

function FormInput({
  label,
  name,
  type = "text",
  value,
  onChange,
  required = false,
}) {
  return (
    <div className="form-field">
      <label htmlFor={name}>
        {label}
        {required && <span className="required">*</span>}
      </label>

      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={`Enter ${label}`}
      />
    </div>
  );
}

function FormSelect({
  label,
  name,
  value,
  onChange,
  options = [],
}) {
  return (
    <div className="form-field">
      <label htmlFor={name}>{label}</label>

      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
      >
        <option value="">Select {label}</option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}